import React, { Suspense, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import DataTable from 'react-data-table-component';
import PeriodSelectBox from '../../global/PeriodSelectBox';
import { fetchCrossSellEarningStats } from '../../../services/Http/Api';
import { useTranslation } from 'react-i18next';
import {
    MdAttachMoney,
    MdPaid,
    MdPeopleAlt,
    MdPercent,
    MdPriceCheck,
    MdTrendingUp,
} from 'react-icons/md';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import StatCard from '../../../shared/layout/cards/StatCard';

const ReactApexChart = React.lazy(() => import('react-apexcharts'));

const CrossSellEarnings = () => {
    const { t } = useTranslation();
    const [period, setPeriod] = useState('last_7_days');
    const [viewMode, setViewMode] = useState('daily');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [sortField, setSortField] = useState('period');
    const [sortDirection, setSortDirection] = useState('asc');
    const [rows, setRows] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [totals, setTotals] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const moneyFormatter = useMemo(() => new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true,
    }), []);

    const numberFormatter = useMemo(() => new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true,
    }), []);

    useEffect(() => {
        setPage(1);
    }, [period, viewMode]);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError(null);

            try {
                const data = await fetchCrossSellEarningStats({
                    period,
                    page,
                    perPage,
                    sortBy: sortField,
                    sortDirection,
                    grouping: viewMode,
                });

                setRows(data?.stats?.data || []);
                setTotalRows(data?.stats?.total || 0);
                setTotals(data?.totals || null);
                setChartData(data?.chart || null);
            } catch (err) {
                console.error(err);
                setError(t('CrossSellEarnings.errors.generic'));
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [period, viewMode, page, perPage, sortField, sortDirection, t]);

    const customStyles = {
        table: { style: { backgroundColor: 'transparent' } },
        headRow: {
            style: {
                backgroundColor: '#f9fafb',
                minHeight: '52px',
                borderBottom: '1px solid #e5e7eb',
            },
        },
        headCells: {
            style: {
                fontSize: '12px',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
            },
        },
        rows: {
            style: {
                minHeight: '72px',
                '&:not(:last-of-type)': {
                    borderBottom: '1px solid #f3f4f6',
                },
                '&:hover': {
                    backgroundColor: '#f9fafb !important',
                    transition: 'background-color 0.2s',
                },
            },
        },
    };

    const columns = useMemo(() => ([
        {
            name: t('CrossSellEarnings.table.columns.period'),
            selector: row => row.label,
            sortable: true,
            sortField: 'period',
            wrap: true,
        },
        {
            name: t('CrossSellEarnings.table.columns.purchase'),
            selector: row => moneyFormatter.format(row.total_purchase_amount || 0),
            sortable: true,
            sortField: 'total_purchase_amount',
        },
        {
            name: t('CrossSellEarnings.table.columns.commission'),
            selector: row => moneyFormatter.format(row.total_commission_earned || 0),
            sortable: true,
            sortField: 'total_commission_earned',
        },
        {
            name: t('CrossSellEarnings.table.columns.fees'),
            selector: row => moneyFormatter.format(row.total_transaction_fee || 0),
            sortable: true,
            sortField: 'total_transaction_fee',
        },
        {
            name: t('CrossSellEarnings.table.columns.transactions'),
            selector: row => row.earning_count,
            sortable: true,
            sortField: 'earning_count',
        },
        {
            name: t('CrossSellEarnings.table.columns.sellers'),
            selector: row => row.distinct_seller_count,
            sortable: true,
            sortField: 'distinct_seller_count',
        },
        {
            name: t('CrossSellEarnings.table.columns.averageCommission'),
            selector: row => `${numberFormatter.format(row.average_commission_percentage || 0)}%`,
            sortable: true,
            sortField: 'average_commission_percentage',
        },
    ]), [moneyFormatter, numberFormatter, t]);

    const handleSort = (column, direction) => {
        setSortField(column.sortField || 'period');
        setSortDirection(direction);
    };

    const chartOptions = useMemo(() => ({
        chart: { type: 'area', toolbar: { show: false } },
        stroke: { curve: 'smooth', width: 3 },
        dataLabels: { enabled: false },
        xaxis: {
            categories: chartData?.labels || [],
            labels: { rotate: -25 },
        },
        yaxis: {
            labels: {
                formatter: (value) => moneyFormatter.format(value || 0),
            },
        },
        tooltip: {
            shared: true,
            y: [
                {
                    formatter: (val) => moneyFormatter.format(val || 0),
                },
                {
                    formatter: (val) => moneyFormatter.format(val || 0),
                },
            ],
        },
        colors: ['#701a82'],
        legend: { position: 'top' },
    }), [chartData, moneyFormatter]);

    const chartSeries = useMemo(() => ([
        {
            name: t('CrossSellEarnings.chart.commission'),
            data: chartData?.commission || [],
        }
    ]), [chartData, t]);

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle icon={<MdTrendingUp />}>{t('CrossSellEarnings.title')}</PageTitle>
                <Controls>
                    <ViewToggle>
                        <ToggleButton
                            type="button"
                            $active={viewMode === 'daily'}
                            onClick={() => setViewMode('daily')}
                        >
                            {t('CrossSellEarnings.view.daily')}
                        </ToggleButton>
                        <ToggleButton
                            type="button"
                            $active={viewMode === 'monthly'}
                            onClick={() => setViewMode('monthly')}
                        >
                            {t('CrossSellEarnings.view.monthly')}
                        </ToggleButton>
                    </ViewToggle>
                    <PeriodSelectBox value={period} onChange={(e) => setPeriod(e.target.value)} />
                </Controls>
            </PageHeader>

            <PageContent>
                {error && <ErrorMessage>{error}</ErrorMessage>}

                <TotalsGrid>
                    <StatCard
                        icon={<MdAttachMoney />}
                        title={t('CrossSellEarnings.totals.totalCommission')}
                        value={moneyFormatter.format(totals?.total_commission_earned || 0)}
                        color="revenue"
                    />
                    <StatCard
                        icon={<MdPaid />}
                        title={t('CrossSellEarnings.totals.totalPurchase')}
                        value={moneyFormatter.format(totals?.total_purchase_amount || 0)}
                        color="neutral"
                    />
                    <StatCard
                        icon={<MdPriceCheck />}
                        title={t('CrossSellEarnings.totals.totalFee')}
                        value={moneyFormatter.format(totals?.total_transaction_fee || 0)}
                        color="neutral"
                    />
                    <StatCard
                        icon={<MdTrendingUp />}
                        title={t('CrossSellEarnings.totals.totalTransactions')}
                        value={totals?.total_transactions || 0}
                        color="transactionValue"
                    />
                    <StatCard
                        icon={<MdPeopleAlt />}
                        title={t('CrossSellEarnings.totals.distinctSellers')}
                        value={totals?.distinct_sellers || 0}
                        color="users"
                    />
                    <StatCard
                        icon={<MdPercent />}
                        title={t('CrossSellEarnings.totals.averageCommission')}
                        value={`${numberFormatter.format(totals?.average_commission_percentage || 0)}%`}
                        color="neutral"
                    />
                </TotalsGrid>

                <StyledCard>
                    <SectionHeader>{t('CrossSellEarnings.chart.title')}</SectionHeader>
                    {chartData?.labels?.length ? (
                        <Suspense fallback={<ChartPlaceholder>{t('common.loading')}</ChartPlaceholder>}>
                            <ReactApexChart
                                options={chartOptions}
                                series={chartSeries}
                                type="area"
                                height={350}
                            />
                        </Suspense>
                    ) : (
                        <EmptyState>{t('CrossSellEarnings.table.empty')}</EmptyState>
                    )}
                </StyledCard>

                <StyledCard noPadding>
                    <div style={{ padding: '1.25rem 1.25rem 0' }}>
                        <SectionHeader>{t('CrossSellEarnings.table.title')}</SectionHeader>
                    </div>
                    <DataTable
                        columns={columns}
                        data={rows}
                        pagination
                        paginationServer
                        paginationPerPage={perPage}
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                        paginationTotalRows={totalRows}
                        onChangePage={setPage}
                        onChangeRowsPerPage={(newPerPage) => {
                            setPerPage(newPerPage);
                            setPage(1);
                        }}
                        highlightOnHover
                        progressPending={loading}
                        sortServer
                        onSort={handleSort}
                        noDataComponent={<EmptyState>{t('CrossSellEarnings.table.empty')}</EmptyState>}
                        customStyles={customStyles}
                    />
                </StyledCard>
            </PageContent>
        </PageContainer>
    );
};

export default CrossSellEarnings;

const Controls = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
`;

const ViewToggle = styled.div`
    display: flex;
    background: #f1f5f9;
    border-radius: 12px;
    padding: 0.25rem;
`;

const ToggleButton = styled.button`
    border: none;
    background: ${({ $active }) => ($active ? '#fff' : 'transparent')};
    color: ${({ $active }) => ($active ? '#701a82' : '#64748b')};
    padding: 0.375rem 1rem;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: ${({ $active }) => ($active ? '0 2px 4px rgba(0,0,0,0.05)' : 'none')};

    &:hover {
        color: #701a82;
    }
`;

const TotalsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 1.5rem;
    margin-bottom: 1.5rem;
`;

const SectionHeader = styled.h3`
    margin: 0 0 1.25rem 0;
    font-size: 1.1rem;
    font-weight: 700;
    color: #1e293b;
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 2.5rem 0;
    color: #64748b;
    font-size: 0.95rem;
`;

const ChartPlaceholder = styled.div`
    text-align: center;
    padding: 2.5rem 0;
    color: #64748b;
`;

const ErrorMessage = styled.div`
    background: #fef2f2;
    color: #b91c1c;
    padding: 1rem;
    border-radius: 12px;
    font-weight: 500;
    margin-bottom: 1.5rem;
    border: 1px solid #fecaca;
`;
